//* 组件库
import type { UiForm, UiTypes } from "various-ui";
import { $loading } from "various-ui";
//* 公共组件
import type { DiagramsOption } from "@/components/visualizations/diagrams/default";

//* 队列数据
interface Task {
    status: string;
    result: string;
    file: string;
    name: string;
    date: string;
    pdf: string;
    id: string;
}

interface Chains {
    name: string;
    value: string;
}

export const useComposable = () => {
    //* 插件
    const i18n = useI18n();

    //* 静态数据
    const vars: { controller?: AbortController; timeout?: NodeJS.Timeout; option: DiagramsOption[] } = {
        option: [
            { key: "name", name: i18n.t("JobTitle"), width: 120 },
            { key: "date", name: i18n.t("CreateTime"), width: 160 },
            { key: "id", name: "ID", width: 120 },
            { key: "status", name: i18n.t("JobStatus"), width: 90 },
            { key: "result", name: i18n.t("DGPred"), width: 200 },
        ],
    };

    //* 响应式数据
    const refs = {
        name: ref<string>(""),
        tasks: ref<Task[]>([]),
        chains: ref<Chains[]>([]),
    };

    //* 表单数据
    const data = reactive({ id: "", name: "", ligand: [], receptor: [], mutation: "" });

    //* 校验规则
    const rules: { [name: string]: UiTypes.verifyRule[] } = {
        id: [
            {
                trigger: "verify",
                verify: () => {
                    if (!data.id) return { verify: false, message: "" };
                    else {
                        return { verify: true, message: "" };
                    }
                },
            },
        ],

        ligand: [
            {
                trigger: "verify",
                verify: () => {
                    if (!data.ligand.length) return { verify: false, message: "" };
                    else {
                        return { verify: true, message: "" };
                    }
                },
            },
        ],

        receptor: [
            {
                trigger: "verify",
                verify: () => {
                    if (!data.receptor.length) return { verify: false, message: "" };
                    else {
                        return { verify: true, message: "" };
                    }
                },
            },
        ],
    };

    //* 节点
    const nodes = {
        file: ref<HTMLInputElement>(),
        form: ref<InstanceType<typeof UiForm>>(),
    };

    //* 函数列表
    const methods = {
        //* 初始化
        init: () => {
            //* 获取缓存数据
            const data = localStorage.getItem("GP-Prediction-Tasks");
            try {
                JSON.parse(data || "[]")?.map((value: any) => {
                    value.file &&
                        refs.tasks.value.push({
                            status: value.status,
                            result: value.result,
                            file: value.file,
                            name: value.name,
                            date: value.date,
                            pdf: value.pdf,
                            id: value.id,
                        });
                });
            } catch {}

            //* 检测是否需要循环获取任务结果
            methods.get_tasks_status(1);
        },

        //* 跳转
        next: (data: Task) => open("/tool/pdb/abview/?url=" + data.pdf, "_blank"),

        //* 重置
        reset: () => {
            //* 表单初始化
            nodes.form.value?.reset();
            //* 数据初始化
            refs.name.value = "";
            refs.chains.value = [];
        },

        //* 文件上传
        upload: (ev: Event) => {
            //* 数据初始化
            const dom = ev.target as HTMLInputElement;
            const file = dom?.files?.[0];
            const result = new FormData();
            //* 检测文件是否存在
            if (!file) dom.value = "";
            else {
                //* 开启加载器
                $loading.open({ message: "" });
                //* 文件读取
                result.append("file", file);
                api.frontend("/api/bio/dg/upload", {
                    data: result,
                    method: "POST",
                    finally: () => {
                        dom.value = "";
                        $loading.close();
                    },
                    success: (response) => {
                        //* 数据更新
                        refs.chains.value = response.chains?.map((value: any) => ({ name: value, value: value }));
                        refs.name.value = file.name;
                        data.receptor = [];
                        data.ligand = [];
                        data.id = response.id;
                        //* 表单校验
                        nodes.form.value?.validator(() => undefined, ["id"]);
                    },
                });
            }
        },

        //* 表单提交
        submit: () => {
            nodes.form.value?.validator((result) => {
                if (!result) return;
                else {
                    //* 加载器
                    $loading.open({ message: "" });
                    //* 接口请求
                    api.frontend("/api/bio/dg", {
                        data: data,
                        method: "POST",
                        error: () => $loading.close(),
                        success: (response) => {
                            //* 任务队列更新
                            refs.tasks.value.push({
                                status: "RUNNING",
                                result: "",
                                file: response,
                                name: data.name || "DG Prediction",
                                date: "",
                                pdf: "",
                                id: "",
                            });

                            //* 重置表单
                            methods.reset();
                            //* 获取任务结果
                            methods.get_tasks_status(1);
                        },
                    });
                }
            });
        },

        get_tasks_status: (delay?: number) => {
            vars.timeout && clearTimeout(vars.timeout);
            vars.timeout = setTimeout(() => {
                const ids = refs.tasks.value.filter((value) => value.status == "RUNNING").map((value) => value.file);
                if (ids.length > 0) {
                    //* 接口控制器
                    vars.controller?.abort("api cancel!!!");
                    vars.controller = new AbortController();
                    //* 接口请求
                    $loading.open({ message: "" });
                    api.frontend("/api/bio/dg/tasks", {
                        data: { ids },
                        signal: vars.controller.signal,
                        method: "POST",
                        finally: () => {
                            $loading.close();
                            vars.controller = undefined;
                        },
                        success: (response) => {
                            //* 数据更新
                            response?.forEach((job: Task) => {
                                const index = refs.tasks.value.findIndex((task) => task.file == job.file);
                                if (index != -1) {
                                    refs.tasks.value[index] = Object.assign(refs.tasks.value[index], job);
                                }
                            });

                            //* 缓存数据
                            localStorage.setItem("GP-Prediction-Tasks", JSON.stringify(refs.tasks.value));
                            //* 检测是否存在未完成的任务
                            if (refs.tasks.value.find((value) => value.status == "RUNNING")) {
                                methods.get_tasks_status();
                            }
                        },
                    });
                }
            }, delay || 5000);
        },
    };

    //* 计算属性
    const computeds = {
        disabled: computed(() => refs.tasks.value.filter((value) => value.status == "RUNNING").length >= 4),
    };

    return { vars, refs, data, rules, nodes, methods, computeds };
};
